import { useMemo, useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { Avatar } from '../../../../layout/Avatar.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import { formatDateTime } from '../../../../shared/format.js';
import { useUsers } from '../../hooks/useUsers.js';
import { useDeleteUser } from '../../hooks/useUserMutations.js';
import { useRoles } from '../../hooks/useRoles.js';
import { UserDrawer } from './UserDrawer.jsx';
import { ResetPasswordModal } from './ResetPasswordModal.jsx';
import { UserPermissionsModal } from './UserPermissionsModal.jsx';
import { ConfirmModal } from '../shared/ConfirmModal.jsx';

function RoleChip({ role }) {
  if (!role) return <span className="text-ink-3 text-xs">—</span>;
  const color = role.color || '#6B7280';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11.5px] font-semibold"
      style={{ background: `${color}1A`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {role.name}
    </span>
  );
}

export function UsersTable() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canCreate = hasPermission('settings.users.create');
  const canEdit = hasPermission('settings.users.edit');
  const canDelete = hasPermission('settings.users.delete');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [permsUser, setPermsUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { data: users = [], isLoading, error } = useUsers();
  const { data: roles = [] } = useRoles();
  const deleteUser = useDeleteUser();

  const rolesById = useMemo(() => {
    const m = new Map();
    roles.forEach((r) => m.set(r._id || r.id, r));
    return m;
  }, [roles]);

  const filtered = useMemo(() => {
    const arr = Array.isArray(users) ? users : [];
    return arr.filter((u) => {
      if (roleFilter) {
        const rid = u.roleId?._id || u.roleId || u.role?._id || u.role?.id;
        if (String(rid) !== String(roleFilter)) return false;
      }
      if (search) {
        const q = search.trim().toLowerCase();
        const blob = `${u.fullName || ''} ${u.username || ''} ${u.email || ''} ${u.phone || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter]);

  function openCreate() {
    setEditing(null);
    setDrawerOpen(true);
  }
  function openEdit(u) {
    setEditing(u);
    setDrawerOpen(true);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <input
              className="input"
              style={{ paddingInlineStart: 32, minWidth: 220 }}
              placeholder="ابحث..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="absolute top-1/2 -translate-y-1/2 right-2.5 text-ink-3">
              <Icon name="search" size={14} />
            </span>
          </div>
          <select
            className="input"
            style={{ minWidth: 180 }}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">جميع الأدوار</option>
            {roles.map((r) => (
              <option key={r._id || r.id} value={r._id || r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        {canCreate ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> مستخدم جديد
          </button>
        ) : null}
      </div>

      {error ? (
        <Card>
          <div className="text-red text-center py-4">تعذّر تحميل المستخدمين</div>
        </Card>
      ) : isLoading ? (
        <Card>
          <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10">لا يوجد مستخدمون</div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-[13px]">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">المستخدم</th>
                <th className="p-2.5 text-start">اسم الدخول</th>
                <th className="p-2.5 text-start">الاتصال</th>
                <th className="p-2.5 text-start">الدور</th>
                <th className="p-2.5 text-start">القاصة</th>
                <th className="p-2.5 text-start">الحالة</th>
                <th className="p-2.5 text-start">آخر دخول</th>
                <th className="p-2.5 text-end">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const id = u._id || u.id;
                const roleObj =
                  u.role && typeof u.role === 'object'
                    ? u.role
                    : rolesById.get(u.roleId?._id || u.roleId);
                return (
                  <tr key={id} className="border-b border-line">
                    <td className="p-2.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar size="sm" name={u.fullName || u.username} id={id} />
                        <div>
                          <div className="font-bold">{u.fullName || u.username}</div>
                          <div className="text-[11px] text-ink-3">{u.email || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2.5 font-mono text-[12px]">@{u.username}</td>
                    <td className="p-2.5 text-[12px] text-ink-2">{u.phone || '—'}</td>
                    <td className="p-2.5">
                      <RoleChip role={roleObj} />
                    </td>
                    <td className="p-2.5 text-[12px] text-ink-2">
                      {u.assignedCashboxId?.name || '—'}
                    </td>
                    <td className="p-2.5">
                      {u.status === 'active' || u.isActive ? (
                        <Pill variant="success">● نشط</Pill>
                      ) : (
                        <Pill variant="muted">معطّل</Pill>
                      )}
                    </td>
                    <td className="p-2.5 text-[12px] text-ink-3">
                      {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : '—'}
                    </td>
                    <td className="p-2.5">
                      <div className="flex items-center gap-1.5 justify-end">
                        {canEdit ? (
                          <button
                            type="button"
                            className="btn"
                            style={{ padding: '4px 8px' }}
                            onClick={() => openEdit(u)}
                            title="تعديل"
                          >
                            <Icon name="edit" size={13} />
                          </button>
                        ) : null}
                        {canEdit ? (
                          <button
                            type="button"
                            className="btn"
                            style={{ padding: '4px 8px' }}
                            onClick={() => setPermsUser(u)}
                            title="تحديد الصلاحيات"
                          >
                            🛡️
                          </button>
                        ) : null}
                        {canEdit ? (
                          <button
                            type="button"
                            className="btn"
                            style={{ padding: '4px 8px' }}
                            onClick={() => setResetUser(u)}
                            title="إعادة تعيين كلمة المرور"
                          >
                            🔑
                          </button>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="btn btn-danger"
                            style={{ padding: '4px 8px' }}
                            onClick={() => setConfirmDelete(u)}
                            title="حذف"
                          >
                            <Icon name="trash" size={13} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <UserDrawer
        open={drawerOpen}
        user={editing}
        roles={roles}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
      />

      <ResetPasswordModal
        open={!!resetUser}
        user={resetUser}
        onClose={() => setResetUser(null)}
      />

      <UserPermissionsModal
        open={!!permsUser}
        user={permsUser}
        onClose={() => setPermsUser(null)}
      />

      <ConfirmModal
        open={!!confirmDelete}
        title="حذف مستخدم"
        message={`هل أنت متأكد من حذف "${confirmDelete?.fullName || confirmDelete?.username || ''}"؟`}
        confirmLabel="حذف"
        variant="danger"
        loading={deleteUser.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteUser.mutateAsync(confirmDelete._id || confirmDelete.id, {
            onSuccess: () => setConfirmDelete(null)
          });
        }}
      />
    </div>
  );
}
